/**
 * DomainRail — the desktop primary nav (lg+): a compact rail of top-level
 * DOMAINS (Customer / Wireless / Hospitality / Switch / Gateway), each fanning out
 * to a panel of its Monitor / Configure sub-items — the Meraki/Ruckus pattern.
 *
 * Fly-outs open on hover AND keyboard focus (CSS group-hover / focus-within), so
 * the interaction is reachable without JS state and works for keyboard users. The
 * tablet thumb-rail (ThumbRailNav) handles touch with the primary sections.
 *
 * Customer menu (2026-10-07): the Customer domain carries the current customer's name under
 * its label and in its fly-out header ("Customer · GTK"), matching the scope bar chip, so the
 * operator always knows WHOSE sites the menu acts on. Its fly-out is two columns (Monitor |
 * Configure) because one column of ~20 rows at the 44px touch floor would run off a laptop
 * screen. Leaves sharing a `subgroup` ("Inventory", "Authentication") sit under a small
 * indented heading. msp-only leaves are filtered by visibleTree() — never rendered at all for
 * org_admin / site_operator (the server re-checks; this only avoids dead ends).
 */
import { useMemo } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSession } from "@/stores/session";
import { orgLabel } from "@/lib/quarantine";
import { NAV_TREE, LEAF_ICONS, type NavDomain, type NavGroup, type NavLeaf } from "./navItems";
import { isLeafActive, scopedHref, visibleTree } from "@/lib/navModel";
import { cn } from "@/lib/cn";

/** Build the concrete href for a leaf under the current scope (see lib/navModel.scopedHref). */
function useScopedHref() {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  return (leaf: NavLeaf): string => scopedHref(leaf, orgId, siteId);
}

/**
 * Is any of this domain's leaves the page currently shown? Same rule as the leaf highlight
 * (lib/navModel.isLeafActive -> activeSection), so a lit domain always has exactly one lit
 * leaf under it (OP#1858). The customer index (/o/:org) resolves to "overview" there.
 */
function useIsDomainActive() {
  const { pathname } = useLocation();
  return (domain: NavDomain) =>
    domain.groups.some((g) => g.items.some((i) => isLeafActive(i, pathname)));
}

/** Domains with more leaves than this get the two-column fly-out. */
const TWO_COLUMN_MIN_LEAVES = 12;

export function DomainRail() {
  const href = useScopedHref();
  const isActive = useIsDomainActive();
  const isMsp = useSession((s) => s.me?.is_msp_admin ?? false);
// Quarantine holding area is labelled, never shown by its stored name (2026-10-19).
  const customerName = useSession((s) => orgLabel(s.currentOrg()));
  // Role-filtered once per role change, not per render.
  const tree = useMemo(() => visibleTree(NAV_TREE, isMsp), [isMsp]);

  return (
    <nav
      aria-label="Primary"
      data-testid="domain-rail"
      className="hidden w-rail shrink-0 border-r border-line bg-surface lg:block"
    >
      <ul className="flex flex-col gap-1 p-2">
        {tree.map((domain) => {
          const first = domain.groups[0]?.items[0];
          const isCustomer = domain.id === "customer";
          const leafCount = domain.groups.reduce((n, g) => n + g.items.length, 0);
          const twoCol = leafCount >= TWO_COLUMN_MIN_LEAVES;
          // MSP group (if visible) spans under both columns rather than taking a third.
          const columns = domain.groups.filter((g) => g.label !== "MSP");
          const msp = domain.groups.find((g) => g.label === "MSP");
          const domainActive = isActive(domain);
          return (
            <li key={domain.id} className="group relative">
              <NavLink
                to={first ? href(first) : "/"}
                // Test hooks (e2e nav spec): which domain this is, and whether it is lit.
                data-nav-domain={domain.id}
                data-active={domainActive ? "true" : undefined}
                className={cn(
                  "flex min-h-touch items-center gap-3 rounded px-3 text-sm font-medium",
                  domainActive
                    ? "bg-brand-soft text-brand"
                    : "text-ink-muted hover:bg-surface-2 group-hover:bg-surface-2",
                )}
              >
                {domain.icon}
                <span className="min-w-0">
                  <span className="block">{domain.label}</span>
                  {/* The customer this menu acts on, echoing the scope bar chip. */}
                  {isCustomer && customerName && (
                    <span className="block truncate text-xs font-normal text-ink-subtle">
                      {customerName}
                    </span>
                  )}
                </span>
                <Caret />
              </NavLink>

              {/* Fly-out panel — opens on hover or keyboard focus within the domain. */}
              <div
                className={cn(
                  "invisible absolute left-full top-0 z-rail opacity-0 transition-opacity",
                  "group-hover:visible group-hover:opacity-100",
                  "group-focus-within:visible group-focus-within:opacity-100",
                  // pl-2 bridges the gap so the mouse can cross onto the panel.
                  "pl-2",
                )}
              >
                <div
                  className={cn(
                    "rounded-lg border border-line bg-overlay p-2 shadow-sheet",
                    // Widths fit the longest OP#1858 labels ("SSID Firewall & Rate Limits", "Gateway Access
                    // Control" + Soon) on one line.
                    twoCol ? "w-[34rem]" : "min-w-64",
                  )}
                >
                  {isCustomer && customerName && (
                    <div className="mb-1 border-b border-line px-2 pb-2 pt-1 text-sm">
                      <span className="text-ink-subtle">Customer</span>
                      <span className="text-ink-subtle"> · </span>
                      <span className="font-medium text-ink">{customerName}</span>
                    </div>
                  )}
                  <div className={cn(twoCol && "grid grid-cols-2 gap-x-2")}>
                    {(twoCol ? columns : domain.groups).map((grp) => (
                      <FlyoutGroup key={grp.label} group={grp} href={href} />
                    ))}
                  </div>
                  {twoCol && msp && (
                    <div className="mt-1 border-t border-line pt-1">
                      <FlyoutGroup group={msp} href={href} />
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** One Monitor / Configure / MSP block, with optional subgroup headings inside it. */
function FlyoutGroup({ group, href }: { group: NavGroup; href: (l: NavLeaf) => string }) {
  // Split the items into runs: a leaf without a subgroup stands alone; consecutive leaves
  // sharing a subgroup render under one indented heading.
  const runs: { subgroup?: string; items: NavLeaf[] }[] = [];
  for (const leaf of group.items) {
    const last = runs[runs.length - 1];
    if (leaf.subgroup && last && last.subgroup === leaf.subgroup) last.items.push(leaf);
    else runs.push({ subgroup: leaf.subgroup, items: [leaf] });
  }
  return (
    <div className="py-1">
      <div className="px-2 pb-1 text-[0.625rem] font-semibold uppercase tracking-wide text-ink-subtle">
        {group.label}
      </div>
      <ul className="flex flex-col">
        {runs.map((run, i) =>
          run.subgroup ? (
            <li key={`${run.subgroup}-${i}`}>
              <div className="px-2 pb-0.5 pt-1.5 text-xs text-ink-subtle">{run.subgroup}</div>
              <ul className="ml-2 flex flex-col border-l border-line pl-1">
                {run.items.map((leaf) => (
                  <FlyoutItem key={leaf.section + leaf.label} leaf={leaf} href={href} />
                ))}
              </ul>
            </li>
          ) : (
            run.items.map((leaf) => (
              <FlyoutItem key={leaf.section + leaf.label} leaf={leaf} href={href} />
            ))
          ),
        )}
      </ul>
    </div>
  );
}

function FlyoutItem({ leaf, href }: { leaf: NavLeaf; href: (l: NavLeaf) => string }) {
  // Active by SECTION, not by exact URL (NavLink `end`): a Site services tab (configure/vlans),
  // a device sheet (devices/:serial) or a filtered list (?severity=) still lights its leaf.
  const { pathname } = useLocation();
  const active = isLeafActive(leaf, pathname);
  return (
    <li>
      <Link
        to={href(leaf)}
        aria-current={active ? "page" : undefined}
        data-nav-leaf={leaf.section}
        className={cn(
          "group/leaf flex min-h-touch items-center justify-between gap-3 rounded px-2 text-sm",
          active ? "bg-brand-soft text-brand" : "text-ink hover:bg-surface-2",
        )}
      >
        <span className="flex items-center gap-2.5">
          {/* Per-item glyph — inherits the row's ink color, dimmed until hover/active. */}
          <span className="text-ink-subtle group-hover/leaf:text-ink-muted">
            {LEAF_ICONS[leaf.section]}
          </span>
          <span>{leaf.label}</span>
        </span>
        {leaf.status === "soon" && (
          <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide text-ink-subtle">
            Soon
          </span>
        )}
      </Link>
    </li>
  );
}

function Caret() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      aria-hidden
      className="ml-auto shrink-0 text-ink-subtle"
    >
      <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
